import {
  productElementCarbonLevelOverlapSlot,
  productElementEpdOverlapSlot,
  productElementOverrideOverlapSlot,
  productElementProductIdOverlapSlot,
} from '@shared/helpers/pipeline/product-element-override-changes.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import { ProjectChangeType } from '@shared/helpers/pipeline/update-engine.models';
import { ProductElementOverrideTarget } from '@shared/models/project.model';
import type { Baseline } from '@shared/models/project.model';
import { propertyEditInvalidatesPriorCategoryEnrich } from '../element-category.helpers';
import type { PropertyChange } from '../element-property.helpers';
import { getElementById, isElement } from '../recursive-element.helpers';
import { undefinedFilter } from '../array.helpers';
import { guards } from '../typeguards.helpers';

/**
 * Describes two condensed changes that targeted the same logical slot; the discarded one lost.
 */
export type ConflictInfo = {
  elementId: string;
  changeType: ProjectChangeType;
  keptChange: ProjectChange;
  discardedChange: ProjectChange;
  reason: string;
};

export type CondenseResult = {
  changes: ProjectChange[];
  conflicts: ConflictInfo[];
};

/** Change types that drop every other edit on the same element when condensed. */
const ELEMENT_DELETION_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementSoftDelete,
  ProjectChangeType.ElementRemove,
]);

const isElementDeletionChange = (change: ProjectChange): boolean =>
  ELEMENT_DELETION_CHANGE_TYPES.has(change.type);

/** Change types that can be absorbed by an {@link ProjectChangeType.ElementActivate} */
const ACTIVATE_ABSORBABLE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyResolved,
  ProjectChangeType.ElementPropertyExpression,
  ProjectChangeType.ElementPropertyFormatted,
  ProjectChangeType.ElementPropertyValue,
  ProjectChangeType.ElementQuantityResolved,
  ProjectChangeType.ElementQuantityExpression,
  ProjectChangeType.ElementQuantityFallbackExpression,
  ProjectChangeType.ElementQuantityFallbackCount,
  ProjectChangeType.ElementQuantityFormatted,
  ProjectChangeType.ElementQuantityValue,
  ProjectChangeType.ElementQuantityCount,
  ProjectChangeType.ElementRecipeApply,
  ProjectChangeType.ElementRecipeDetach,
  ProjectChangeType.ElementRecipeClear,
  ProjectChangeType.ElementCategory,
  ProjectChangeType.ElementAdd,
  ProjectChangeType.ProductElementEpd,
  ProjectChangeType.ProductElementTransportOverride,
  ProjectChangeType.ProductElementWasteOverride,
  ProjectChangeType.ProductElementCarbonLevelOverride,
  ProjectChangeType.ProductElementCountResolved,
  ProjectChangeType.ProductElementCountExpression,
  ProjectChangeType.ProductElementCountFormatted,
  ProjectChangeType.ProductElementProductId,
]);

/**
 * Unique slot key for property-level merge detection.
 * Changes sharing a slot key on the same element are candidates for merge/conflict.
 */
export const getPropertySlotKey = (
  change: ProjectChange,
): string | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementPropertyResolved:
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementPropertyValue:
    case ProjectChangeType.ElementPropertyFallbackCount:
    case ProjectChangeType.ElementPropertyCount:
      // Align with {@link getOverlapSlotKey} `property:${name}` so mixed edit types
      // (e.g. count vs resolved) on the same field condense and subtract consistently.
      return `property:${change.propertyName}`;
    case ProjectChangeType.ElementPropertyFormatted:
      return `${change.type}:${change.propertyName}`;
    case ProjectChangeType.ElementQuantityResolved:
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
    case ProjectChangeType.ElementQuantityFallbackCount:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityCount:
      // Align with {@link getOverlapSlotKey} `quantity:${name}` so mixed edit types
      // (e.g. expression vs resolved) on the same quantity condense and subtract consistently.
      return `quantity:${change.quantityName}`;
    case ProjectChangeType.ElementQuantityFormatted:
      return `${change.type}:${change.quantityName}`;
    case ProjectChangeType.ElementCategoryEnrich:
      return change.type;
    case ProjectChangeType.ProductElementCountResolved:
    case ProjectChangeType.ProductElementCountExpression:
    case ProjectChangeType.ProductElementCountFormatted:
    case ProjectChangeType.ProductElementProductId:
      return productElementProductIdOverlapSlot();
    case ProjectChangeType.ProductElementEpd:
      return productElementEpdOverlapSlot();
    case ProjectChangeType.ProductElementTransportOverride:
      return productElementOverrideOverlapSlot(
        ProductElementOverrideTarget.TRANSPORT_KM,
      );
    case ProjectChangeType.ProductElementWasteOverride:
      return productElementOverrideOverlapSlot(
        ProductElementOverrideTarget.WASTE_PERCENT,
      );
    case ProjectChangeType.ProductElementCarbonLevelOverride:
      return productElementCarbonLevelOverlapSlot();
    case ProjectChangeType.ElementRecipeApply:
    case ProjectChangeType.ElementRecipeDetach:
    case ProjectChangeType.ElementRecipeClear:
      return 'recipe';
    case ProjectChangeType.ElementCategory:
    case ProjectChangeType.ElementDeactivate:
    case ProjectChangeType.ElementActivate:
    case ProjectChangeType.ElementSoftDelete:
    case ProjectChangeType.ElementQuantitySelect:
    case ProjectChangeType.ElementQuantityAutoUpdate:
      return change.type;
    case ProjectChangeType.ElementDataTablePropertiesApply:
      return `${change.type}:${change.dataTable.id}`;
    case ProjectChangeType.ElementName:
    case ProjectChangeType.ElementRemove:
      return change.type;
    default:
      return undefined;
  }
};

const getElementId = (change: ProjectChange): string | undefined =>
  'elementId' in change ? change.elementId : undefined;

/**
 * Element property edits that can change which category-defined properties exist
 * (e.g. switching heating type adds or removes COP).
 */
const CATEGORY_STRUCTURE_DRIVER_PROPERTY_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyResolved,
  ProjectChangeType.ElementPropertyExpression,
  ProjectChangeType.ElementPropertyValue,
  ProjectChangeType.ElementPropertyCount,
  ProjectChangeType.ElementPropertyFallbackCount,
]);

/**
 * Element ids whose incoming batch changes category structure but does not include a
 * new {@link ProjectChangeType.ElementCategoryEnrich}. Prior enrich rows for those
 * elements are stale (e.g. user reverted the select that triggered enrichment).
 */
export const getIncomingCategoryEnrichElementIds = (
  changes: readonly ProjectChange[],
): Set<string> => {
  const ids = new Set<string>();
  for (const change of changes) {
    if (change.type !== ProjectChangeType.ElementCategoryEnrich) continue;
    const elementId = getElementId(change);
    if (elementId) ids.add(elementId);
  }
  return ids;
};

/** Extract the new value from a category property driver change for tree reshape checks. */
const getDriverPropertyCountFromChange = (
  change: ProjectChange,
): PropertyChange['count'] | undefined => {
  switch (change.type) {
    case ProjectChangeType.ElementPropertyCount:
      return change.count;
    case ProjectChangeType.ElementPropertyResolved:
      return change.value;
    case ProjectChangeType.ElementPropertyValue:
      return change.overrideValue;
    case ProjectChangeType.ElementPropertyExpression:
      return change.expression;
    case ProjectChangeType.ElementPropertyFallbackCount:
      return change.fallbackCount;
    default:
      return undefined;
  }
};

/**
 * True when a property edit can add or remove category property rows (not product-only
 * selectors such as `amount_scrap_based`). Without {@link version}, any category property
 * edit is treated as a driver for backward-compatible condense-only callers.
 */
export const isCategoryPropertyTreeDriverChange = (
  change: ProjectChange,
  version?: Baseline,
): boolean => {
  if (change.type === ProjectChangeType.ElementCategory) return true;
  if (!CATEGORY_STRUCTURE_DRIVER_PROPERTY_TYPES.has(change.type)) {
    return false;
  }
  if (!('propertyName' in change)) return false;
  const propertyName = change.propertyName;
  if (typeof propertyName !== 'string') return false;
  if (!version) return true;

  const elementId = getElementId(change);
  if (!elementId) return false;
  const element = getElementById(version, elementId, false);
  if (!element || !isElement(element)) return false;

  return propertyEditInvalidatesPriorCategoryEnrich(
    element,
    propertyName,
    getDriverPropertyCountFromChange(change),
  );
};

export const getElementsWithCategoryStructureDriverWithoutEnrich = (
  changes: readonly ProjectChange[],
  version?: Baseline,
): Set<string> => {
  const withIncomingEnrich = new Set<string>();
  const driverElements = new Set<string>();

  for (const change of changes) {
    const elementId = getElementId(change);
    if (!elementId) continue;

    if (change.type === ProjectChangeType.ElementCategoryEnrich) {
      withIncomingEnrich.add(elementId);
      continue;
    }

    if (isCategoryPropertyTreeDriverChange(change, version)) {
      driverElements.add(elementId);
    }
  }

  for (const elementId of withIncomingEnrich) {
    driverElements.delete(elementId);
  }

  return driverElements;
};

export type CondenseOptions = {
  /**
   * Indices in {@link changes} where a new logical batch starts. At each boundary, the
   * pending {@link ProjectChangeType.ElementActivate} tracker is cleared so a property
   * change in a later batch is **not** absorbed into an earlier batch's activate.
   *
   * Use when condensing the union of multiple {@link Action.changes} arrays
   * (e.g. {@link resolveVersionForProposal}) so cross-action edits survive condense.
   * Within a single batch, absorb still applies (same as the no-boundary call).
   *
   * Indices outside `[0, changes.length)` are ignored. `0` is allowed but redundant.
   */
  actionBoundaryIndices?: readonly number[];
  /** When set, only category template driver properties invalidate prior enrich rows. */
  version?: Baseline;
};

/**
 * Optimizes a change list by merging redundant entries and surfaces merge conflicts.
 *
 * Rules applied (in insertion order):
 * - **Activate absorb:** {@link ProjectChangeType.ElementActivate} absorbs subsequent
 *   property/product/recipe changes on the same element. Limited to the same logical
 *   batch when {@link CondenseOptions.actionBoundaryIndices} is supplied.
 * - **Property merge:** Later change on the same element + slot replaces the earlier one.
 * - **Override merge:** granular override changes on the same slot keep the latest row.
 * - **Visibility last wins:** At most one {@link ProjectChangeType.ElementActivate} or
 *   {@link ProjectChangeType.ElementDeactivate} per element; a later visibility change replaces
 *   the earlier one. Matching baseline is dropped later by {@link dropNoOpChangesAgainstBaseline}.
 * - **Deletion supersedes:** {@link ProjectChangeType.ElementSoftDelete} and
 *   {@link ProjectChangeType.ElementRemove} remove all other changes on the same element;
 *   later changes on that element are dropped (including across {@link CondenseOptions.actionBoundaryIndices}).
 * - **Category driver without enrich:** A category-driving property (or category) edit
 *   drops earlier {@link ProjectChangeType.ElementCategoryEnrich} on that element when
 *   the batch does not include a replacement enrich (selection reverted).
 * - **One enrich per element:** At most one {@link ProjectChangeType.ElementCategoryEnrich}
 *   per elementId is emitted (latest wins).
 *
 * @param changes - Raw changes (typically one or multiple actions' batches)
 * @param options - {@link CondenseOptions} (optional)
 * @returns Condensed changes and any detected conflicts
 */
export const condenseChanges = (
  changes: ProjectChange[],
  options?: CondenseOptions,
): CondenseResult => {
  const version = options?.version;
  if (changes.length === 0) return { changes: [], conflicts: [] };

  const boundarySet =
    options?.actionBoundaryIndices && options.actionBoundaryIndices.length > 0
      ? new Set(options.actionBoundaryIndices)
      : undefined;

  const conflicts: ConflictInfo[] = [];

  // Track positions of activate/deactivate per element (last visibility change wins)
  const activateIndex = new Map<string, number>();
  const deactivateIndex = new Map<string, number>();
  const softDeleteIndex = new Map<string, number>();

  // Per-element slot -> index in result for property merge
  const slotIndex = new Map<string, number>();

  const result: (ProjectChange | null)[] = [];

  for (let i = 0; i < changes.length; i++) {
    if (boundarySet?.has(i)) {
      // Crossing into a new action: pending activates should not absorb later actions' edits.
      // Slot tracking persists so cross-action slot replace/merge keeps working.
      // Keep visibility index maps so a later action can still replace an earlier activate/deactivate
      // already emitted in `result` (last wins across actions).
      activateIndex.clear();
    }
    const change = changes[i];
    if (change === undefined) continue;
    const elementId = getElementId(change);
    if (!elementId) {
      result.push(change);
      continue;
    }

    const pendingSoftDeleteIdx = softDeleteIndex.get(elementId);
    const hasActiveSoftDelete =
      pendingSoftDeleteIdx !== undefined &&
      result[pendingSoftDeleteIdx] !== null;

    if (hasActiveSoftDelete && !isElementDeletionChange(change)) {
      continue;
    }
    if (pendingSoftDeleteIdx !== undefined && !hasActiveSoftDelete) {
      softDeleteIndex.delete(elementId);
    }

    if (isElementDeletionChange(change)) {
      clearAllChangesForElement(
        result,
        elementId,
        slotIndex,
        activateIndex,
        deactivateIndex,
        softDeleteIndex,
      );
      const idx = result.length;
      result.push(change);
      softDeleteIndex.set(elementId, idx);
      continue;
    }

    // Visibility: last activate/deactivate per element wins (baseline no-op drop is downstream)
    if (change.type === ProjectChangeType.ElementActivate) {
      clearPriorElementVisibility(
        result,
        elementId,
        slotIndex,
        activateIndex,
        deactivateIndex,
      );
      const idx = result.length;
      result.push(change);
      activateIndex.set(elementId, idx);
      continue;
    }

    if (change.type === ProjectChangeType.ElementDeactivate) {
      clearPriorElementVisibility(
        result,
        elementId,
        slotIndex,
        activateIndex,
        deactivateIndex,
      );
      const idx = result.length;
      result.push(change);
      deactivateIndex.set(elementId, idx);
      continue;
    }

    // Activate absorb: if the element has a pending activate, absorb qualifying changes
    const actIdx = activateIndex.get(elementId);
    if (
      actIdx !== undefined &&
      result[actIdx] !== null &&
      ACTIVATE_ABSORBABLE_TYPES.has(change.type)
    ) {
      // Absorbed into the activate — don't emit as separate change
      // But still track for property-level slot merge within absorbed changes
      const slotKey = getPropertySlotKey(change);
      if (slotKey) {
        const fullKey = `${elementId}:${slotKey}`;
        slotIndex.set(fullKey, -1); // -1 = absorbed, don't emit
      }
      continue;
    }

    if (isCategoryPropertyTreeDriverChange(change, version)) {
      clearPriorCategoryEnrichChanges(result, elementId, slotIndex);
    }

    if (change.type === ProjectChangeType.ElementCategoryEnrich) {
      clearPriorCategoryEnrichChanges(result, elementId, slotIndex);
      const idx = result.length;
      result.push(change);
      slotIndex.set(
        `${elementId}:${ProjectChangeType.ElementCategoryEnrich}`,
        idx,
      );
      continue;
    }

    // Property-level slot merge
    const slotKey = getPropertySlotKey(change);
    if (slotKey) {
      const fullKey = `${elementId}:${slotKey}`;
      const existingIdx = slotIndex.get(fullKey);
      const existing = guards.is.number(existingIdx)
        ? result[existingIdx]
        : undefined;

      if (guards.is.number(existingIdx) && guards.is.defined(existing)) {
        const slotDetail =
          'propertyName' in change ? ` (${change.propertyName})` : '';
        conflicts.push({
          elementId,
          changeType: change.type,
          keptChange: change,
          discardedChange: existing,
          reason: `Duplicate ${change.type}${slotDetail} on element "${elementId}" — keeping latest`,
        });

        // Other property types: replace with latest
        result[existingIdx] = null;
      }

      const idx = result.length;
      result.push(change);
      slotIndex.set(fullKey, idx);
      continue;
    }

    result.push(change);
  }

  return {
    changes: undefinedFilter(result),
    conflicts,
  };
};

/** Drop prior {@link ProjectChangeType.ElementCategoryEnrich} rows for one element. */
const clearPriorCategoryEnrichChanges = (
  result: (ProjectChange | null)[],
  elementId: string,
  slotIndex: Map<string, number>,
): void => {
  for (let i = 0; i < result.length; i++) {
    const existing = result[i];
    if (
      existing?.type === ProjectChangeType.ElementCategoryEnrich &&
      getElementId(existing) === elementId
    ) {
      result[i] = null;
    }
  }
  slotIndex.delete(`${elementId}:${ProjectChangeType.ElementCategoryEnrich}`);
};

/** Remove all slot entries belonging to a given element (used when an activate is cancelled). */
/**
 * Removes any pending {@link ProjectChangeType.ElementActivate} or
 * {@link ProjectChangeType.ElementDeactivate} for {@link elementId} so the next visibility
 * change becomes the sole row for that element.
 */
const clearPriorElementVisibility = (
  result: (ProjectChange | null)[],
  elementId: string,
  slotIndex: Map<string, number>,
  activateIndex: Map<string, number>,
  deactivateIndex: Map<string, number>,
): void => {
  const deactIdx = deactivateIndex.get(elementId);
  if (deactIdx !== undefined && result[deactIdx] !== null) {
    result[deactIdx] = null;
  }
  deactivateIndex.delete(elementId);

  const actIdx = activateIndex.get(elementId);
  if (actIdx !== undefined && result[actIdx] !== null) {
    result[actIdx] = null;
    clearSlotsForElement(slotIndex, elementId);
  }
  activateIndex.delete(elementId);
};

const clearSlotsForElement = (
  slotIndex: Map<string, number>,
  elementId: string,
): void => {
  const prefix = `${elementId}:`;
  for (const key of slotIndex.keys()) {
    if (key.startsWith(prefix)) {
      slotIndex.delete(key);
    }
  }
};

/**
 * Nullifies every emitted change for {@link elementId} and clears per-element tracking maps.
 * Used when {@link ProjectChangeType.ElementSoftDelete} supersedes all prior proposal edits.
 */
const clearAllChangesForElement = (
  result: (ProjectChange | null)[],
  elementId: string,
  slotIndex: Map<string, number>,
  activateIndex: Map<string, number>,
  deactivateIndex: Map<string, number>,
  softDeleteIndex: Map<string, number>,
): void => {
  for (let i = 0; i < result.length; i++) {
    const existing = result[i];
    if (!existing) continue;
    if (getElementId(existing) === elementId) {
      result[i] = null;
    }
  }
  clearSlotsForElement(slotIndex, elementId);
  activateIndex.delete(elementId);
  deactivateIndex.delete(elementId);
  softDeleteIndex.delete(elementId);
};
